import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const DemoModeContext = createContext();

export function useDemoMode() {
    return useContext(DemoModeContext);
}

export function DemoModeProvider({ children }) {
    const [isDemoMode, setIsDemoMode] = useState(true); // Default to demo mode
    const [demoMessages, setDemoMessages] = useState([]);
    const [demoInsights, setDemoInsights] = useState([]);
    const [demoAgentActivities, setDemoAgentActivities] = useState([]);
    const [isLoadingDemoData, setIsLoadingDemoData] = useState(false);

    // Load demo data from Firestore when demo mode is enabled
    useEffect(() => {
        if (isDemoMode) {
            loadDemoData();
        }
    }, [isDemoMode]);

    const loadDemoData = async () => {
        setIsLoadingDemoData(true);
        try {
            console.log('📦 Loading demo data from Firestore...');

            // Load messages
            const messagesQuery = query(
                collection(db, 'messages'),
                orderBy('timestamp', 'desc'),
                limit(100)
            );
            const messagesSnapshot = await getDocs(messagesQuery);
            const messages = messagesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
            }));
            setDemoMessages(messages);
            console.log(`✅ Loaded ${messages.length} demo messages`);

            // Load insights
            const insightsQuery = query(
                collection(db, 'insights'),
                orderBy('timestamp', 'desc'),
                limit(20)
            );
            const insightsSnapshot = await getDocs(insightsQuery);
            const insights = insightsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
            }));
            setDemoInsights(insights);
            console.log(`✅ Loaded ${insights.length} demo insights`);

            // Load agent activities
            const activitiesQuery = query(
                collection(db, 'agent_activity'),
                orderBy('timestamp', 'desc'),
                limit(100)
            );
            const activitiesSnapshot = await getDocs(activitiesQuery);
            const activities = activitiesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
            }));
            setDemoAgentActivities(activities);
            console.log(`✅ Loaded ${activities.length} demo agent activities`);

            console.log('🎉 Demo data loaded successfully!');
        } catch (error) {
            console.error('❌ Error loading demo data:', error);
        } finally {
            setIsLoadingDemoData(false);
        }
    };

    const toggleDemoMode = () => {
        setIsDemoMode(prev => !prev);
        console.log(`🎭 Demo Mode ${!isDemoMode ? 'ENABLED' : 'DISABLED'}`);
    };

    const value = {
        isDemoMode,
        setIsDemoMode,
        toggleDemoMode,
        demoMessages,
        demoInsights,
        demoAgentActivities,
        isLoadingDemoData,
        loadDemoData,
    };

    return (
        <DemoModeContext.Provider value={value}>
            {children}
        </DemoModeContext.Provider>
    );
}

